import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtUSD, fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const INSTRUMENTS = ["EUR/USD","GBP/USD","USD/JPY","AUD/USD","USD/CAD","USD/CHF","NZD/USD","XAU/USD"];

const META = {
  "EUR/USD": { multiplier: 100000, decimals: 5, kind: "usd_quote" },
  "GBP/USD": { multiplier: 100000, decimals: 5, kind: "usd_quote" },
  "AUD/USD": { multiplier: 100000, decimals: 5, kind: "usd_quote" },
  "NZD/USD": { multiplier: 100000, decimals: 5, kind: "usd_quote" },
  "USD/JPY": { multiplier: 100000, decimals: 3, kind: "usd_base" },
  "USD/CAD": { multiplier: 100000, decimals: 5, kind: "usd_base" },
  "USD/CHF": { multiplier: 100000, decimals: 5, kind: "usd_base" },
  "XAU/USD": { multiplier: 100,    decimals: 2, kind: "metal"    },
};

function previewPnL(trade) {
  const m = META[trade.instrument];
  if (!m) return 0;
  const close = parseFloat(trade.close_price);
  const entry = parseFloat(trade.entry_price);
  const lots = parseFloat(trade.lots);
  if (isNaN(close) || isNaN(entry) || isNaN(lots)) return 0;
  const dir = trade.side === "buy" ? 1 : -1;
  const diff = (close - entry) * dir;
  if (m.kind === "metal") return diff * lots * m.multiplier;
  if (m.kind === "usd_base" && close > 0) return (diff * lots * m.multiplier) / close;
  return diff * lots * m.multiplier;
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) {
  return v ? new Date(v).toISOString() : null;
}

export default function EditTradeDialog({ trade, open, onOpenChange, onSaved, onClose }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const isClosed = trade?.status === "closed";

  useEffect(() => {
    if (trade) {
      setForm({
        instrument: trade.instrument,
        side: trade.side,
        lots: String(trade.lots),
        entry_price: String(trade.entry_price),
        stop_loss: trade.stop_loss ? String(trade.stop_loss) : "",
        take_profit: trade.take_profit ? String(trade.take_profit) : "",
        close_price: trade.close_price != null ? String(trade.close_price) : "",
        pnl: trade.pnl != null ? String(trade.pnl) : "",
        opened_at: isoToLocalInput(trade.opened_at),
        closed_at: isoToLocalInput(trade.closed_at),
        comment: trade.comment || "",
        pnl_manual: false,
      });
    }
  }, [trade]);

  // Auto-recompute pnl from close_price unless user typed pnl manually
  useEffect(() => {
    if (!form || !isClosed || form.pnl_manual) return;
    const p = previewPnL(form);
    setForm((f) => f && { ...f, pnl: p.toFixed(2) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.close_price, form?.entry_price, form?.side, form?.lots, form?.instrument]);

  if (!trade || !form) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        instrument: form.instrument,
        side: form.side,
        lots: parseFloat(form.lots),
        entry_price: parseFloat(form.entry_price),
        stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null,
        take_profit: form.take_profit ? parseFloat(form.take_profit) : null,
        opened_at: localInputToIso(form.opened_at),
        comment: form.comment,
      };
      if (isClosed) {
        payload.close_price = form.close_price ? parseFloat(form.close_price) : null;
        payload.pnl = form.pnl ? parseFloat(form.pnl) : 0;
        payload.closed_at = localInputToIso(form.closed_at);
      }
      await api.patch(`/admin/trades/${trade.id}`, payload);
      toast.success("Trade updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save changes");
    } finally {
      setBusy(false);
    }
  };

  const runningPnL = isClosed ? parseFloat(form.pnl) || 0 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-subtle max-w-lg max-h-[90vh] p-0 flex flex-col" data-testid="edit-trade-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Edit Trade — {trade.instrument}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Modify any field. {isClosed ? "Changing amount or price on a closed trade automatically reconciles the client's balance." : "Trade remains open after save."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-2 overflow-y-auto flex-1">
          <div className="text-xs text-muted-foreground">
            Client: <span className="text-foreground font-medium">{trade.user_name || trade.user_id}</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Instrument</Label>
              <Select value={form.instrument} onValueChange={(v) => setForm({ ...form, instrument: v })}>
                <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="edit-tr-instrument"><SelectValue /></SelectTrigger>
                <SelectContent>{INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Side</Label>
              <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="edit-tr-side"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">BUY</SelectItem>
                  <SelectItem value="sell">SELL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Lots</Label>
              <Input type="number" step="0.01" value={form.lots} onChange={(e) => setForm({ ...form, lots: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-lots" />
            </div>
            <div>
              <Label className="label-eyebrow">Entry Price</Label>
              <Input type="number" step="0.00001" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-entry" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Stop Loss</Label>
              <Input type="number" step="0.00001" value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-sl" />
            </div>
            <div>
              <Label className="label-eyebrow">Take Profit</Label>
              <Input type="number" step="0.00001" value={form.take_profit} onChange={(e) => setForm({ ...form, take_profit: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-tp" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Opened At</Label>
            <Input type="datetime-local" value={form.opened_at} onChange={(e) => setForm({ ...form, opened_at: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-opened" />
          </div>

          {isClosed && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Close Price</Label>
                  <Input type="number" step="0.00001" value={form.close_price} onChange={(e) => setForm({ ...form, close_price: e.target.value, pnl_manual: false })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-close-price" />
                </div>
                <div>
                  <Label className="label-eyebrow">Closed At</Label>
                  <Input type="datetime-local" value={form.closed_at} onChange={(e) => setForm({ ...form, closed_at: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-closed-at" />
                </div>
              </div>

              <div>
                <Label className="label-eyebrow">P&L (USD) — auto-computed, override to force</Label>
                <Input type="number" step="0.01" value={form.pnl} onChange={(e) => setForm({ ...form, pnl: e.target.value, pnl_manual: true })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-tr-pnl" />
                <p className={`text-xs mt-1.5 font-mono-num ${runningPnL >= 0 ? "text-profit" : "text-loss"}`}>
                  Will book {fmtUSD(runningPnL, { signed: true })} to client balance (delta from previous).
                </p>
              </div>
            </>
          )}

          <div>
            <Label className="label-eyebrow">Comment</Label>
            <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="mt-2 bg-background border-subtle h-10" data-testid="edit-tr-comment" />
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-3 border-t border-subtle shrink-0 gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-tr-cancel">Cancel</Button>
          <div className="flex gap-2">
            {!isClosed && onClose && (
              <Button
                variant="outline"
                onClick={() => { onOpenChange(false); onClose(trade); }}
                className="border-loss text-loss hover:bg-loss hover:text-white"
                data-testid="edit-tr-close-trade"
              >
                Close Trade…
              </Button>
            )}
            <Button onClick={submit} disabled={busy} data-testid="edit-tr-save">
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
